<template>
  <div>
    <div class="row">
      <div class="col-6">
        <div class="heading required">Book Pickup</div>
      </div>
      <div class="col-6 pt-4">
        <div class="custom-alert pr-2 pl-2">
          <small v-if="isBeforeCutoffTime">Same-day pickup <b>available</b>.</small>
          <small v-else>Same-day pickup <b>not available</b>.</small>
          <small>
            You are able to book same-day pickup
            <b>before {{ cutoffTime?.format('HH:mm') }}</b>
          </small>
        </div>
      </div>
    </div>

    <div class="months-row d-flex" :class="{ centered: false, 'bold-font': true }">
      <div v-if="showPrevArrow" class="arrows prev-arrow" @click="prevMonth" />
      <div>{{ startMonth }} - {{ endMonth }} {{ year }}</div>
      <div v-if="showNextArrow" class="arrows next-arrow" @click="nextMonth" />
    </div>

    <div class="date-items d-flex">
      <div v-if="showPrevArrow" class="arrows prev-arrow" @click="prevWeek" />

      <div
        v-for="(date, index) in dateList"
        :key="'date_' + index"
        :class="{
          'date-item_disabled': date.disabled,
          'date-item_active': selectedDate === date.date,
        }"
        class="date-item"
        @click="() => selectDate(date.date)"
      >
        <div class="date-item__body">
          <span class="date-item__day">
            {{ date.day }}
          </span>
          <span class="date-item__month">
            {{ date.month }}
          </span>
        </div>
        <span class="date-item__weekday">
          {{ date.weekday }}
        </span>
      </div>

      <div v-if="showNextArrow" class="arrows next-arrow" @click="nextWeek" />
    </div>

    <div v-if="pickupError" class="mt-3 pickup-required-msg">Please select Pickup Date</div>
  </div>
</template>

<script lang="ts">
import type { PickupDate } from '@/types/Models/Pickup'
import { defineComponent, ref, computed, onMounted, type PropType, type ComputedRef } from 'vue'
import moment from 'moment'

export default defineComponent({
  name: 'PickupCalendar',
  props: {
    dates: {
      type: Array as PropType<PickupDate[]>,
      required: true,
    },
    maxPeriod: {
      type: Number,
      default: 0,
    },
    pickupError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select-date'],
  setup(props, { emit }) {
    const selectedDate = ref<string | null>(null)
    const startDate = ref<Date | null>(null)
    const show = ref<boolean>(false)

    const pickupDates: ComputedRef<PickupDate[]> = computed(() => props.dates || [])
    const dateList: ComputedRef<any[]> = computed(() => {
      const currentDate = startDate.value || new Date()
      const dates = []

      for (let i = 0; i <= 7; i += 1) {
        const date = new Date(currentDate.setDate(currentDate.getDate() + (i === 0 ? 0 : 1)))
        dates.push({
          day: date.getDate(),
          month: date.toLocaleString('default', { month: 'short' }),
          weekday: date.toLocaleDateString('default', { weekday: 'short' }),
          date: moment(date).format('YYYY-MM-DD'),
          disabled: isDisabled(date),
        })
      }

      return dates
    })

    const startMonth = computed(() => moment(dateList.value[0]?.date).format('MMMM'))
    const endMonth = computed(() => moment(dateList.value[dateList.value.length - 1]?.date).format('MMMM'))
    const year = computed(() => moment(dateList.value[0]?.date).format('YYYY'))
    const showPrevArrow = computed(() => !isBeforeOrToday(dateList.value[0]?.date || moment().format('YYYY-MM-DD')))
    const showNextArrow = computed(
      () => !isExceedMaxDate(dateList.value[dateList.value.length - 1]?.date || moment().format('YYYY-MM-DD')),
    )
    const cutoffTime = computed(() => moment().set({ hour: 12, minute: 0, second: 0 }))
    const isBeforeCutoffTime = computed(() => moment().isBefore(cutoffTime.value))

    const selectDate = (date: string) => {
      selectedDate.value = date
      emit('select-date', date)
    }

    const isBeforeOrToday = (date: string) => moment().format('YYYY-MM-DD') >= date
    const isYesterdayOrBefore = (date: string) => moment().format('YYYY-MM-DD') > date
    const isExceedMaxDate = (date: string) => {
      if (props.maxPeriod > 0) {
        const diffDays = Math.ceil((new Date(date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        return diffDays > props.maxPeriod
      }
      return false
    }

    const isDisabled = (date: Date) => {
      const dateData = {
        day: date.getDate(),
        month: date.toLocaleString('default', { month: 'short' }),
        weekday: date.toLocaleDateString('default', { weekday: 'short' }),
        date: moment(date).format('YYYY-MM-DD'),
      }
      const pickupDate = pickupDates.value?.find(
        (pickupDate: PickupDate) => pickupDate.date === moment(date).format('YYYY-MM-DD'),
      )
      return (
        !pickupDate?.isAvailable ||
        isExceedMaxDate(dateData.date) ||
        (isBeforeCutoffTime.value ? isYesterdayOrBefore(dateData.date) : isBeforeOrToday(dateData.date))
      )
    }

    const nextWeek = () => {
      startDate.value = new Date(dateList.value.slice(-1)[0]?.date || moment().format('YYYY-MM-DD'))
    }

    const prevWeek = () => {
      const date = new Date(dateList.value[0]?.date || moment().format('YYYY-MM-DD'))
      startDate.value = new Date(date.setDate(date.getDate() - 8))
    }

    const nextMonth = () => {
      const today = new Date()
      const date = new Date(dateList.value.slice(-1)[0]?.date || moment().format('YYYY-MM-DD'))
      const nextMonth =
        date.getMonth() === 11
          ? new Date(date.getFullYear() + 1, 0, 1)
          : new Date(date.getFullYear(), date.getMonth() + 1, 1)

      startDate.value =
        nextMonth.getFullYear() === today.getFullYear() && nextMonth.getMonth() === today.getMonth()
          ? new Date()
          : nextMonth
    }

    const prevMonth = () => {
      const today = new Date()
      const date = new Date(dateList.value[0]?.date || moment().format('YYYY-MM-DD'))
      const prevMonth =
        date.getMonth() === 0
          ? new Date(date.getFullYear() - 1, 11, 1)
          : new Date(date.getFullYear(), date.getMonth() - 1, 1)

      startDate.value =
        prevMonth.getFullYear() === today.getFullYear() && prevMonth.getMonth() === today.getMonth()
          ? new Date()
          : prevMonth
    }

    onMounted(() => {
      selectDate(
        dateList.value.find((date: any, key) => date.disabled === false && key > 0)?.date ||
          moment().format('YYYY-MM-DD'),
      )
    })

    return {
      selectedDate,
      startDate,
      show,
      dateList,
      startMonth,
      endMonth,
      year,
      showPrevArrow,
      showNextArrow,
      cutoffTime,
      isBeforeCutoffTime,
      selectDate,
      nextWeek,
      prevWeek,
      nextMonth,
      prevMonth,
    }
  },
})
</script>
